import React, { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, Check, Copy, FileText, FolderKanban, MessageSquare, SquareCheckBig } from 'lucide-react';
import { appLink, siteOf } from '../../lib/answerParts';
import { WEB_URL } from '../../native/platform';
import { useOptionalTask } from '../../contexts/TaskContext';

/**
 * An AI answer that arrives as Markdown (bots, the assistant, a task's bot result),
 * set for reading in a chat rather than as a document page: a calm heading scale,
 * lists with quiet markers, tables in a frame, code blocks with their language and
 * a copy button. Links into the app open in place and show what they point to (a
 * document, a task, a project) as a soft chip; links elsewhere are underlined in the
 * brand color with an arrow, so both stand apart from the text without shouting.
 */

const ORIGINS = [...new Set([window.location.origin, (() => {
  try { return new URL(WEB_URL).origin; } catch { return null; }
})()].filter(Boolean))];

const KIND_ICON = { document: FileText, task: SquareCheckBig, project: FolderKanban, chat: MessageSquare };
const KIND_LABEL = { document: 'quick_create.document', task: 'quick_create.task', project: 'common.project', chat: 'nav.create.chat' };

const textOf = (children) => React.Children.toArray(children).map((c) => (typeof c === 'string' ? c : '')).join('');
// A link written as its own address (https://github.com/...): the text says nothing the
// address does not, so it shows as what it is (the site, or the thing in the app).
const isBare = (href, children) => {
  const text = textOf(children).trim().replace(/\/$/, '');
  const bare = (href || '').replace(/\/$/, '');
  return !!text && (text === bare || text === bare.replace(/^https?:\/\//, '') || text === bare.replace(/^mailto:/, ''));
};

const AnswerLink = ({ href, children, title }) => {
  const { t } = useTranslation();
  const task = useOptionalTask();
  // A link to nowhere ("...", "#"), which models write when they have no address: just text.
  if (!/^(https?:|mailto:|tel:|\/(?!\/))/i.test(href || '')) return <>{children}</>;
  const inApp = appLink(href, ORIGINS);
  const bare = isBare(href, children);
  if (inApp) {
    const Icon = KIND_ICON[inApp.kind];
    // A task opens in its popup over the chat, as in the step list: the person stays in
    // the conversation (a new tab, with Ctrl or Cmd, still opens the task's page).
    const openTask = inApp.kind === 'task' && inApp.id && task ? (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault();
      task.openTaskDetail(inApp.id);
    } : undefined;
    return (
      <Link to={inApp.to} title={title} onClick={openTask} data-testid="answer-app-link"
        className="px-1 -mx-px rounded-md bg-brand-soft/70 text-brand-ink font-medium no-underline [box-decoration-break:clone] [-webkit-box-decoration-break:clone] hover:bg-brand-soft transition-colors">
        {Icon && <Icon className="inline-block w-[0.95em] h-[0.95em] mr-1 -mt-0.5 align-middle text-brand-icon" aria-hidden="true" />}
        {bare && KIND_LABEL[inApp.kind] ? t(KIND_LABEL[inApp.kind]) : children}
      </Link>
    );
  }
  const site = siteOf(href);
  if (bare && site) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" title={href} data-testid="answer-site-link"
        className="inline-flex items-center gap-0.5 align-baseline h-5 px-1.5 rounded-md bg-muted text-[11.5px] font-medium text-muted-foreground no-underline hover:bg-accent hover:text-foreground transition-colors">
        {site}
        <ArrowUpRight className="w-3 h-3 opacity-70" aria-hidden="true" />
      </a>
    );
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" title={title || site || undefined}
      className="font-medium text-brand-ink underline decoration-brand-icon/35 decoration-1 underline-offset-[3px] hover:decoration-brand-icon transition-colors">
      {children}
      {/^https?:/i.test(href || '') && (
        <ArrowUpRight className="inline-block w-[0.85em] h-[0.85em] ml-px -mt-0.5 align-middle opacity-60" aria-hidden="true" />
      )}
    </a>
  );
};

// Code is highlighted once highlight.js is loaded (its own chunk, only when an answer has code).
let lowlightReady = null;
const loadLowlight = () => {
  if (!lowlightReady) {
    lowlightReady = import('lowlight').then(({ common, createLowlight }) => createLowlight(common)).catch(() => null);
  }
  return lowlightReady;
};
const toReact = (node, key) => {
  if (node.type === 'text') return node.value;
  const kids = (node.children || []).map((c, i) => toReact(c, i));
  if (node.type === 'root') return kids;
  return <span key={key} className={(node.properties?.className || []).join(' ')}>{kids}</span>;
};

const SHELL = /^(bash|sh|shell|zsh|console|terminal|powershell|ps1?)$/;

/**
 * What the copy button copies: the code as shown, except that a terminal session
 * ("$ npm install" followed by its output) copies only the commands, without "$ ",
 * so it can be pasted into a terminal as is.
 */
export const copyableCode = (language, code) => {
  const lines = code.split('\n');
  if (!SHELL.test(language || '') || !lines.some((l) => /^\s*[$>] /.test(l))) return code;
  return lines.filter((l) => /^\s*[$>] /.test(l)).map((l) => l.replace(/^\s*[$>] /, '')).join('\n');
};

const writeClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {  // no clipboard API (an older webview, a page without https): the old way
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.cssText = 'position:fixed;top:0;left:0;opacity:0';
    document.body.appendChild(area);
    area.select();
    let ok;
    try { ok = document.execCommand('copy'); } catch { ok = false; }
    area.remove();
    return ok;
  }
};

const CodeBlock = ({ language, code }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [highlighted, setHighlighted] = useState(null);
  useEffect(() => {
    let alive = true;
    setHighlighted(null);
    loadLowlight().then((lowlight) => {
      if (!alive || !lowlight) return;
      try {
        const tree = language && lowlight.registered(language)
          ? lowlight.highlight(language, code) : lowlight.highlightAuto(code);
        setHighlighted(toReact(tree));
      } catch { /* plain text it stays */ }
    });
    return () => { alive = false; };
  }, [language, code]);
  const copy = async () => {
    if (await writeClipboard(copyableCode(language, code))) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };
  // overflow-clip (not hidden) keeps the header sticky: in a long block the copy
  // button stays at hand while scrolling through the code.
  return (
    <div className="answer-code my-3 rounded-xl border border-border bg-muted/40 overflow-clip" data-testid="answer-code">
      <div className="sticky top-0 z-[1] flex items-center justify-between h-8 pl-3.5 pr-1.5 border-b border-border/70 bg-muted">
        <span className="font-mono text-[11px] text-muted-foreground lowercase">{language || 'text'}</span>
        <button type="button" onClick={copy} data-testid="answer-code-copy"
          className="inline-flex items-center gap-1 h-6 px-1.5 rounded-md text-[11.5px] text-muted-foreground hover:text-foreground hover:bg-accent transition-colors">
          {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
          {t(copied ? 'common.copied' : 'common.copy')}
        </button>
      </div>
      <pre className="m-0 px-3.5 py-3 overflow-x-auto text-[12.5px] leading-[1.6]">
        <code className="font-mono">{highlighted || code}</code>
      </pre>
    </div>
  );
};

const components = {
  h1: ({ node, ...p }) => <h3 className="text-[16.5px] font-semibold tracking-tight text-foreground mt-5 mb-2" {...p} />,
  h2: ({ node, ...p }) => <h3 className="text-[15.5px] font-semibold tracking-tight text-foreground mt-5 mb-1.5" {...p} />,
  h3: ({ node, ...p }) => <h4 className="text-[14.5px] font-semibold text-foreground mt-4 mb-1" {...p} />,
  h4: ({ node, ...p }) => <h5 className="text-[14px] font-semibold text-foreground mt-3 mb-1" {...p} />,
  h5: ({ node, ...p }) => <h6 className="text-[13px] font-semibold text-muted-foreground mt-3 mb-1" {...p} />,
  h6: ({ node, ...p }) => <h6 className="text-[13px] font-semibold text-muted-foreground mt-3 mb-1" {...p} />,
  p: ({ node, ...p }) => <p className="my-2" {...p} />,
  ul: ({ node, className, ...p }) => (
    <ul className={`my-2 pl-5 space-y-1 ${className === 'contains-task-list' ? 'list-none pl-0' : 'list-disc marker:text-brand-icon/70'}`} {...p} />
  ),
  ol: ({ node, ...p }) => <ol className="my-2 pl-5 space-y-1 list-decimal marker:text-muted-foreground marker:font-medium" {...p} />,
  li: ({ node, className, ...p }) => (
    <li className={`pl-1 [&>ul]:my-1 [&>ol]:my-1 ${className === 'task-list-item' ? 'flex items-start gap-2 pl-0' : ''}`} {...p} />
  ),
  // GFM checklists ("- [x] done"): a quiet box instead of the browser's checkbox.
  input: ({ checked, type }) => (type === 'checkbox' ? (
    <span aria-hidden="true" className={`mt-[3px] w-4 h-4 shrink-0 rounded-[5px] border flex items-center justify-center ${
      checked ? 'bg-brand-icon border-brand-icon text-white dark:text-background' : 'border-muted-foreground/40'}`}>
      {checked && <Check className="w-3 h-3" strokeWidth={3} />}
    </span>
  ) : null),
  strong: ({ node, ...p }) => <strong className="font-semibold text-foreground" {...p} />,
  em: ({ node, ...p }) => <em className="italic" {...p} />,
  del: ({ node, ...p }) => <del className="text-muted-foreground" {...p} />,
  a: ({ href, title, children }) => <AnswerLink href={href} title={title}>{children}</AnswerLink>,
  // react-markdown 10 no longer tells inline from block code: a block is the code
  // inside <pre> (it has a language or line breaks); the rest is inline.
  code: ({ node, className, children, ...rest }) => {
    const text = String(children ?? '');
    const lang = /language-([\w+#.-]+)/.exec(className || '')?.[1];
    if (!lang && !text.includes('\n')) {
      return (
        <code className="px-1.5 py-[1px] rounded-md bg-muted border border-border/60 font-mono text-[0.86em] text-foreground" {...rest}>
          {children}
        </code>
      );
    }
    return <CodeBlock language={lang ? lang.toLowerCase() : ''} code={text.replace(/\n$/, '')} />;
  },
  pre: ({ children }) => <>{children}</>,
  table: ({ node, ...p }) => (
    <div className="my-3 overflow-x-auto rounded-xl border border-border" data-testid="answer-table">
      <table className="w-full border-collapse text-[13px] tabular-nums" {...p} />
    </div>
  ),
  thead: ({ node, ...p }) => <thead className="bg-muted/50" {...p} />,
  th: ({ node, style, ...p }) => (
    <th style={style} className="px-3 py-2 text-left font-medium text-[12px] text-muted-foreground border-b border-border whitespace-nowrap" {...p} />
  ),
  tr: ({ node, ...p }) => <tr className="border-b border-border/60 last:border-0 even:bg-muted/20" {...p} />,
  td: ({ node, style, ...p }) => <td style={style} className="px-3 py-2 align-top" {...p} />,
  blockquote: ({ node, ...p }) => (
    <blockquote className="my-3 border-l-2 border-brand-icon/45 pl-3.5 text-muted-foreground [&>p]:my-1" {...p} />
  ),
  hr: () => <hr className="my-5 border-border" />,
  img: ({ alt, src }) => (
    <img src={src} alt={alt || ''} loading="lazy" className="my-2 max-w-full max-h-96 rounded-xl border border-border" />
  ),
};

// textClass: the size of the text (a bot's chat bubble reads larger).
const MarkdownMessage = ({ children, className = '', textClass = 'text-[14px] leading-[1.65]' }) => {
  const text = useMemo(() => (typeof children === 'string' ? children : ''), [children]);
  if (!text) return null;
  return (
    <div className={`answer-md ${textClass} text-foreground break-words [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 ${className}`}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {text}
      </ReactMarkdown>
    </div>
  );
};

// Memoized: a chat re-renders on every key typed in its composer and every live event of a
// running bot, and parsing every answer's Markdown again each time made long chats slow on phones.
export default React.memo(MarkdownMessage);

// For tests: the parts of an answer, without the Markdown parser.
export { AnswerLink, CodeBlock };
